import React, { useContext, useMemo, useState } from "react";
import cx from "classnames";
import { Accordion, Button, Link, Loading, Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { IGroup, IGroupValue, ILeague } from "@modules/vacancies/contextVacancies/ContextVacancies";
import { VacanciesType } from "@modules/vacancies/Vacancies";
import { ContextApp } from "@config/Context/contextApp";
import GroupsTable from "../../../Groups/groups-table";
import LinkComponentsTable from "../link-components-table";
import useContextUrl from "@common/hooks/useContextUrl";
import styles from "./linkCard.module.scss";
import InstructorsTable from "@modules/vacancies/Instructor/components/instructors-table";
import GroupDeleteModal from "@modules/vacancies/Groups/group-delete-modal";
import useDisableGroup from "@modules/vacancies/Groups/hooks/useDisableGroup";
import GroupVisibilityModal from "@modules/vacancies/Groups/group-visibility-modal";
import useToggleGroupState from "@modules/vacancies/Groups/hooks/useToggleGroupState";
import useUpdateGroupIsActive from "@modules/vacancies/hooks/useUpdateGroupIsActive";
import GroupActiveModal from "@modules/vacancies/Groups/group-active-modal";
import i18n from "@config/i18n";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

interface LinkCardProps {
  link: ILeague;
  tableType: VacanciesType;
  userCanEdit?: boolean;
  isEditing?: boolean;
  hasGroupSections?: boolean;
  onUpdateGroupValues?: (values: IGroupValue[]) => void;
  onUpdateRetakerValues?: (values: IGroupValue[]) => void;
  onUpdateInstructorValues?: (values: IGroupValue[]) => void;
  onCreateGroup?: () => void;
  onMigrateGroup?: (groupId?: string | number) => void;
  setValidationArray?: (validations: any[]) => void;
  onAssignClientCode?: () => void;
}

const itemCode = (item: ILeague) => {
  return (
    <Tooltip
      className={styles.headerCard_code__underline}
      label={i18n.t("bundle.link-card.system-code-tooltip")}
      placement="right"
    >
      {item?.clientCode ? <b>{item?.code}</b> : `(${item.code})`}
    </Tooltip>
  );
};

const itemClientCode = (item: ILeague) => {
  return (
    <Tooltip
      className={styles.headerCard_code__underline}
      label={i18n.t("bundle.link-card.user-code-tooltip")}
      placement="right"
    >
      ({i18n.t("bundle.link-card.user-code")} <b>{item?.clientCode}</b>)
    </Tooltip>
  );
};

const LinkCard = ({
  link,
  userCanEdit,
  tableType,
  isEditing,
  hasGroupSections = false,
  onUpdateGroupValues,
  onUpdateRetakerValues,
  onUpdateInstructorValues,
  onCreateGroup,
  onMigrateGroup,
  setValidationArray,
  onAssignClientCode,
}: LinkCardProps) => {
  const history = useHistory();
  const contextApp = useContext(ContextApp);
  const { origin, scenario, workspace } = useContextUrl();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [showGroupActiveModal, setShowGroupActiveModal] = useState(false);
  const [showGroupVisibilityModal, setShowGroupVisibilityModal] = useState(false);
  const [showGroupDeleteModal, setShowGroupDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null);

  const { isLoading, handleDisableGroup } = useDisableGroup({
    selectedGroup,
    onValidationArray: setValidationArray,
  });

  const { isLoading: isUpdatingVisibility, handleToggleGroupVisibility } = useToggleGroupState({
    selectedGroup,
    onValidationArray: setValidationArray,
  });

  const [{ isLoading: isUpdatingActiveState }, updateGroupIsActive] = useUpdateGroupIsActive({
    onValidationArray: setValidationArray,
    onSuccess(group) {
      if (group) {
        selectedGroup.openGroup = Boolean(group?.visibleForEnrollment);
        selectedGroup.isActive = Boolean(group?.isActive);
      }
    },
  });

  const unassigmentInstructors = useMemo(() => {
    if (link.blocksWithoutInstructorAssigment > 0) {
      const blockPercent = (link.blocksWithoutInstructorAssigment * 100) / link.totalBlocks;

      return {
        ...link,
        id: "unassignment",
        instructor: {
          id: "unassignment",
          code: "",
          name: t("bundle.link-card.instructor-table.without-instructor-assigned"),
          contract: null,
        },
        totalBlocks: link.blocksWithoutInstructorAssigment,
        link: null,
        blocksPercentage: Math.round(blockPercent) || 0,
      };
    }

    return null;
  }, [link, language]);

  return (
    <>
      {(isLoading || isUpdatingVisibility || isUpdatingActiveState) && <Loading />}

      <Accordion
        data-linkColor={link.color}
        type="neutral"
        className={cx(styles.linkCard, styles[`linkCard__${link.color}`])}
        title={
          ((
            <>
              <div className={styles.linkCard_circle} />
              {`${t("dimension.link.title")} ${link.codeName}`}
            </>
          ) as unknown) as string
        }
        customContent={
          <div className={styles.linkCard_customHeader}>
            <Link
              onClick={() =>
                history.push(
                  `/scheduler/editor/link/${workspace}/${scenario}/${origin}/${link?.linkId}`,
                )
              }
              className={styles.linkCode}
            >
              {itemCode(link)} {!!link?.clientCode && itemClientCode(link)}
            </Link>

            {contextApp?.user?.abilities?.can_edit_links_and_sections && !link?.clientCode && (
              <Button
                variant="ghost"
                onClick={e => {
                  e.stopPropagation();
                  onAssignClientCode?.();
                }}
                className={styles.codeManagement}
              >
                (<Icon className={styles.icon} name="edit" size="sm" />{" "}
                {t("bundle.link-card.add-code")})
              </Button>
            )}

            {userCanEdit && link?.course?.edhCanEdit && (
              <Link
                onClick={() =>
                  history.push(
                    `/editor/form-edit/${workspace}/${scenario}/${origin}/${link?.linkId}`,
                  )
                }
                className={styles.scheduleEdit}
                iconRight="external-link"
              >
                {t("bundle.link-card.edit-schedule")}
              </Link>
            )}

            {userCanEdit && !!link?.course?.generatesPackages && (
              <Link
                onClick={() =>
                  history.push(
                    `/editor/packages-edit/${workspace}/${scenario}/${origin}/${link?.linkId}`,
                  )
                }
                className={styles.packageEdit}
                iconRight="external-link"
              >
                {t("bundle.link-card.edit-packages")}
              </Link>
            )}
          </div>
        }
        opened
      >
        <div className={styles.linkCard_content}>
          {tableType === VacanciesType.vacancies && (
            <GroupsTable
              linkId={link?.linkId}
              groups={link?.groups}
              edhCanEdit={link?.course?.edhCanEdit}
              isEditing={isEditing}
              hasGroupSections={hasGroupSections}
              onCreateGroup={onCreateGroup}
              onToggleActive={group => {
                setSelectedGroup(group);
                setShowGroupActiveModal(true);
              }}
              onToggleVisibility={group => {
                setSelectedGroup(group);
                setShowGroupVisibilityModal(true);
              }}
              onDeleteGroup={group => {
                setSelectedGroup(group);
                setShowGroupDeleteModal(true);
              }}
              onMigrateGroup={(group: IGroup) => onMigrateGroup?.(group?.id)}
              onChangeInputValue={onUpdateGroupValues}
              onChangeRetakerCapacity={onUpdateRetakerValues}
            />
          )}

          {tableType === VacanciesType.instructor && (
            <InstructorsTable
              instructors={
                unassigmentInstructors
                  ? [...(link?.instructors ?? []), unassigmentInstructors]
                  : link?.instructors
              }
              linkTotalBlocks={link?.totalBlocks}
              linkId={link?.linkId}
              isEditing={isEditing}
              link={link}
              onUpdateValues={onUpdateInstructorValues}
            />
          )}

          <LinkComponentsTable
            sections={link?.sections}
            showTitle={tableType === VacanciesType.vacancies}
          />
        </div>
      </Accordion>

      <GroupDeleteModal
        isOpen={showGroupDeleteModal}
        groupLabel={selectedGroup?.label}
        onCancel={() => {
          setShowGroupDeleteModal(false);
          setSelectedGroup(null);
        }}
        onConfirm={() => {
          handleDisableGroup();
          setShowGroupDeleteModal(false);
        }}
      />

      <GroupVisibilityModal
        isOpen={showGroupVisibilityModal}
        selectedGroup={selectedGroup}
        onCancel={() => {
          setShowGroupVisibilityModal(false);
          setSelectedGroup(null);
        }}
        onConfirm={() => {
          handleToggleGroupVisibility();
          setShowGroupVisibilityModal(false);
        }}
      />

      <GroupActiveModal
        isOpen={showGroupActiveModal}
        selectedGroup={selectedGroup}
        groups={link?.groups}
        onCancel={() => {
          setShowGroupActiveModal(false);
          setSelectedGroup(null);
        }}
        onConfirm={() => {
          updateGroupIsActive(selectedGroup?.id, !selectedGroup?.isActive);
          setShowGroupActiveModal(false);
        }}
      />
    </>
  );
};

export default LinkCard;
